import React, { useEffect, useState } from 'react'
import { useGalaxyStore } from '../../../core/state/GalaxyStore'
import { formatHashrate, formatBitcoin, formatTime, formatPercentage } from '../../utils/formatters'
import { animateValue } from '../../utils/animations'
import { apiManager } from '../../../core/data/APIManager'
import { logger } from '../../../core/utils/Logger'
import './NetworkStats.css'

export interface NetworkStatsProps {
  className?: string
  mode?: 'compact' | 'detailed' | 'graphical'
}

export const NetworkStats: React.FC<NetworkStatsProps> = ({ className = '', mode = 'detailed' }) => {
  const networkStats = useGalaxyStore(state => state.network)
  const currentHeight = useGalaxyStore(state => state.network.currentHeight)
  const transactionCount = useGalaxyStore(state => state.blockchain?.transactions?.size || 0)
  const mempoolSize = useGalaxyStore(state => state.mempool?.size || 0)
  const mempoolTxCount = useGalaxyStore(state => state.mempool?.count || 0)
  const mempoolState = useGalaxyStore(state => state.mempool)
  // Get most recent block timestamp for next block calculation
  const mostRecentBlock = useGalaxyStore(state => {
    const blocks = Array.from(state.blockchain?.blocks?.values() || [])
    return blocks.sort((a, b) => b.height - a.height)[0]
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Fetch initial data and set up real-time updates
  useEffect(() => {
    let isMounted = true

    const fetchInitialData = async () => {
      try {
        if (apiManager && apiManager.isInitialized) {
          logger.info('Fetching initial network stats', 'NetworkStats')
          await apiManager.fetchNetworkStats()
          if (isMounted) {
            setIsLoading(false)
            setError(null)
          }
        } else {
          logger.warn('API Manager not ready, using mock data', 'NetworkStats')
          setIsLoading(false)
        }
      } catch (err) {
        logger.error('Failed to fetch network stats', 'NetworkStats', err as Error)
        if (isMounted) {
          setError('Failed to fetch network data')
          setIsLoading(false)
        }
      }
    }

    fetchInitialData()

    return () => {
      isMounted = false
    }
  }, [])

  const [displayValues, setDisplayValues] = useState({
    hashrate: networkStats?.hashrate || 0,
    difficulty: networkStats?.difficulty || 0,
    blockHeight: currentHeight
  })

  useEffect(() => {
    const hashrateEl = document.getElementById('hashrate-value')
    if (hashrateEl && networkStats?.hashrate && displayValues.hashrate !== networkStats.hashrate) {
      animateValue(hashrateEl, displayValues.hashrate, networkStats.hashrate, 1000)
      setDisplayValues(prev => ({ ...prev, hashrate: networkStats.hashrate }))
    }
  }, [networkStats?.hashrate, displayValues.hashrate])

  const getHashrateTrend = () => {
    // For now, return stable since we don't have 24h change tracking
    return 'stable'
  }

  const estimateNextBlock = () => {
    const avgBlockTime = 600 // 10 minutes in seconds
    // Use actual last block timestamp instead of last network stats update
    const lastBlockTime = mostRecentBlock?.timestamp || Date.now()
    const timeSinceLastBlock = Date.now() - lastBlockTime
    const estimatedTime = Math.max(0, avgBlockTime * 1000 - timeSinceLastBlock)
    return estimatedTime
  }

  const [nextBlockTime, setNextBlockTime] = useState(estimateNextBlock())

  useEffect(() => {
    const timer = setInterval(() => {
      setNextBlockTime(estimateNextBlock())
    }, 1000)

    return () => clearInterval(timer)
  }, [mostRecentBlock?.timestamp])

  const difficultyAdjustmentProgress = () => {
    const blocksPerAdjustment = 2016
    const blocksIntoEpoch = currentHeight % blocksPerAdjustment
    const progress = (blocksIntoEpoch / blocksPerAdjustment) * 100
    const blocksRemaining = blocksPerAdjustment - blocksIntoEpoch
    return { progress, blocksRemaining }
  }

  const { progress: diffProgress, blocksRemaining } = difficultyAdjustmentProgress()

  // Simple fee estimation based on mempool congestion
  const getFeeEstimates = () => {
    const baseFee = 1
    const congestionMultiplier = mempoolState?.congestionLevel === 'high' ? 3 : 
                                mempoolState?.congestionLevel === 'medium' ? 2 : 1
    
    return {
      economyFee: baseFee * congestionMultiplier,
      halfHourFee: baseFee * congestionMultiplier * 2,
      fastestFee: baseFee * congestionMultiplier * 4
    }
  }

  const feeEstimates = getFeeEstimates()

  // Format mempool size in MB with proper units
  const formatMempoolSize = (sizeInMB: number) => {
    if (sizeInMB < 1) {
      return `${(sizeInMB * 1024).toFixed(1)} KB`
    }
    return `${sizeInMB.toFixed(1)} MB`
  }

  // Determine which fee tier should be recommended based on mempool congestion
  const getRecommendedFeeTier = () => {
    if (mempoolState?.congestionLevel === 'high') return 'high'
    if (mempoolState?.congestionLevel === 'low') return 'low'
    return 'medium' // default for normal/medium congestion
  }

  const recommendedTier = getRecommendedFeeTier()

  if (error) {
    return (
      <div className={`network-stats ${mode} ${className} error`}>
        <h2>Network Status</h2>
        <div className="error-message">
          <span>⚠️ {error}</span>
          <small>Using cached/mock data</small>
        </div>
      </div>
    )
  }

  return (
    <div className={`network-stats ${mode} ${className} ${isLoading ? 'loading' : ''}`}>
      <h2>Network Status {isLoading && <span className="hud-loading"></span>}</h2>

      <div className="stats-grid">
        <div className="stat-item">
          <span className="hud-label">Hashrate</span>
          <div className="stat-value">
            <span id="hashrate-value" className="hud-value">
              {formatHashrate(networkStats?.hashrate || 0)}
            </span>
            <span className={`trend-indicator ${getHashrateTrend()}`}>
              {getHashrateTrend() === 'up' ? '▲' : getHashrateTrend() === 'down' ? '▼' : ''}
            </span>
          </div>
          <span className="stat-change">
            +0.0%
          </span>
        </div>

        <div className="stat-item">
          <span className="hud-label">Block Height</span>
          <div className="stat-value">
            <span className="hud-value">#{currentHeight.toLocaleString()}</span>
          </div>
          <span className="stat-detail">Next in ~{formatTime(nextBlockTime / 1000)}</span>
        </div>

        <div className="stat-item">
          <span className="hud-label">Difficulty</span>
          <div className="stat-value">
            <span className="hud-value">{((networkStats?.difficulty || 0) / 1e12).toFixed(2)}T</span>
          </div>
          <div className="difficulty-progress">
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: `${diffProgress}%` }} />
            </div>
            <span className="stat-detail">{blocksRemaining} blocks to adjustment</span>
          </div>
        </div>

        <div className="stat-item">
          <span className="hud-label">Fee Market</span>
          <div className="fee-grid">
            <div className={`fee-tier ${recommendedTier === 'low' ? 'recommended' : ''}`}>
              <span className="fee-label">Low</span>
              <span className="fee-value">
                {feeEstimates.economyFee} sat/vB
              </span>
            </div>
            <div className={`fee-tier ${recommendedTier === 'medium' ? 'recommended' : ''}`}>
              <span className="fee-label">Med</span>
              <span className="fee-value">
                {feeEstimates.halfHourFee} sat/vB
              </span>
            </div>
            <div className={`fee-tier ${recommendedTier === 'high' ? 'recommended' : ''}`}>
              <span className="fee-label">High</span>
              <span className="fee-value">
                {feeEstimates.fastestFee} sat/vB
              </span>
            </div>
          </div>
        </div>

        <div className="stat-item">
          <span className="hud-label">Activity</span>
          <div className="activity-stats">
            <div className="activity-row">
              <span>Transactions</span>
              <span className="hud-value">{transactionCount}</span>
            </div>
            <div className="activity-row">
              <span>Mempool</span>
              <span className="hud-value">{formatMempoolSize(mempoolSize)} ({mempoolTxCount.toLocaleString()} txs)</span>
            </div>
          </div>
        </div>
      </div>

      {mode === 'detailed' && (
        <div className="hud-divider" />
      )}
    </div>
  )
}